import * as yup from "yup";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../assets/utils/lib";
import { Constants } from "../../../assets/utils/lib/constants";
import { LookupDto } from "../../../assets/utils/lib/lookupDto";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import Drawer from "../../../assets/vfx/drawer/drawer";
import Form from "../../../assets/vfx/form/form";
import { CommunityNeighborhoodDto } from "../../../services/dto";
import TagInput from "../../common/TagInput/TagInput";

interface Props {
  data?: CommunityNeighborhoodDto;
  show: boolean;
  counties: LookupDto[] | undefined;
  isLoading: boolean;
  onHide: () => void;
  onSave: (values: CommunityNeighborhoodDto) => DrawerFormSaveResult;
}

const validationSchema = yup.object().shape({
  name: yup.string().trim().required("Name is required."),
  countyId: yup.string().required("County is required.").notOneOf([Constants.emptyGuid], "County is required."),
  zipCodes: yup
    .array()
    .of(
      yup
        .string()
        .required("ZIP code is required.")
        .matches(/^\d{5}(?:-\d{4})?$/, "Enter a valid ZIP code."),
    )
    .required("At least one ZIP code is required.")
    .min(1, "At least one ZIP code is required."),
});

export const CommunityNeighborhoodForm = (props: Props) => {
  const isNew = !props.data?.id || props.data.id === Constants.emptyGuid;
  const countyOptions: DropdownOption[] = LookupDto.mapToOptions(props.counties ?? []);
  const handleSubmit = (values: CommunityNeighborhoodDto) =>
    Promise.resolve(props.onSave(values)).then((result) => {
      if (!hasClientErrorOperationStatusCode(result)) props.onHide();
    });

  return (
    <Drawer.Form
      className="w-30"
      show={props.show}
      backdrop
      placement="end"
      scroll
      title={`${isNew ? "New" : "Edit"} Neighborhood`}
      initialValues={props.data ?? { zipCodes: [], isDeleted: false }}
      validationSchema={validationSchema}
      enableReinitialize
      on_Submit={handleSubmit}
      onHide={props.onHide}
    >
      <Form.PanelSection
        iconClassName="bi bi-info-circle"
        title="1. Basic Information"
      >
        <Form.Text
          name="name"
          label="Name"
          placeholder="Enter neighborhood name"
          isLoading={props.isLoading}
        />
        <Form.TextArea
          name="description"
          label="Description"
          rows={3}
          isLoading={props.isLoading}
        />
      </Form.PanelSection>

      <Form.PanelSection
        iconClassName="bi bi-geo-alt"
        title="2. Location"
        style={{ marginTop: 16 }}
      >
        <Form.Dropdown
          name="countyId"
          label="County"
          blankOption={{
            key: Constants.emptyGuid,
            display: "Select County",
            value: Constants.emptyGuid,
          }}
          options={countyOptions}
          isLoading={props.isLoading}
        />
        <Form.Group label="ZIP Codes">
          <TagInput
            name="zipCodes"
            itemLabel="ZIP code"
            helperText="Enter a ZIP code and press Enter"
            placeholder="Add ZIP code..."
          />
        </Form.Group>
      </Form.PanelSection>

      {!isNew && (
        <Form.PanelSection
          iconClassName="bi bi-toggle-on"
          title="3. Status"
          style={{ marginTop: 16 }}
        >
          <Form.Checkbox name="isDeleted" label="Deleted" />
        </Form.PanelSection>
      )}
    </Drawer.Form>
  );
};
